import { getSubmissionsDataApi, getSubmissionsFilterOptionApi } from 'api/submissions-database';
import { createContext, useContext, useEffect, useState } from 'react';
import staticSubmissionFiltersOptions from 'constants/staticSubmissionFiltersOptions';

export const SubmissionsContext = createContext({
  submissionsData: [],
  filtersOptions: {},
  setSubmissionData: () => {},
  hiddenColumns: ['Based on', 'Submitted by', 'Read', 'Responded', 'Priority'],
  setHiddenColumns: () => {},
  filters: {},
  setFilters: () => {},
  updateFilters: () => {},
  loading: true,
  setLoading: () => {},
  filteredData: [],
  fetchMoreSubmissions: () => {},
  hasMore: false,
  resetSubmissionData: () => {},
  fetchSubmissions: () => {},
  searchText: '',
  setSearchText: () => {},
  sortingCriteria: { sortBy: '', sortOrder: 1 },
  setSortingCriteria: () => {},
  fetchAndSetFilterOption: () => {}
});

export const useSubmissionsContext = () => useContext(SubmissionsContext);

export function SubmissionsContextProvider({ children }) {
  const [submissionsData, setSubmissionsData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState(['Based on', 'Submitted by', 'Read', 'Responded', 'Priority']);
  const [filters, setFilters] = useState({ isArchived: false });
  const [filtersOptions, setFiltersOption] = useState(staticSubmissionFiltersOptions);
  const [loading, setLoading] = useState(true);
  const [nextPage, setNextPage] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [sortingCriteria, setSortingCriteria] = useState({ sortBy: '', sortOrder: 1 });

  const updateFilters = (type, operator, value) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      const isEmptyArray = Array.isArray(value) && value.length === 0;

      if (isEmptyArray) {
        delete newFilters[type];
      } else {
        newFilters[type] = value;
      }

      return newFilters;
    });
  };

  const fetchAndSetFilterOption = async () => {
    const res = await getSubmissionsFilterOptionApi();
    setFiltersOption((prev) => ({ ...prev, ...res?.data }));
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setSubmissionsData([]);
      const { sortBy, sortOrder } = sortingCriteria;
      const response = await getSubmissionsDataApi(1, filters, searchText, sortBy, sortOrder);
      const newData = response?.data?.data || [];
      const newNextPage = response?.data?.next_page;
      const newHasMore = !response?.data?.is_end;

      setSubmissionsData((prev) => [...prev, ...newData]);
      setNextPage(newNextPage || null);
      setHasMore(newHasMore ?? true);
    } catch (error) {
      console.error('Error fetching more submissions:', error);
      setHasMore(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreSubmissions = async () => {
    if (!hasMore) return;
    try {
      const { sortBy, sortOrder } = sortingCriteria;
      const response = await getSubmissionsDataApi(
        nextPage,
        filters,
        searchText,
        sortBy,
        sortOrder
      );
      const newData = response?.data?.data || [];
      const newNextPage = response?.data?.next_page;
      const newHasMore = !response?.data?.is_end;

      setSubmissionsData((prev) => [...prev, ...newData]);
      setNextPage(newNextPage || null);
      setHasMore(newHasMore ?? true);
    } catch (error) {
      console.error('Error fetching more submissions:', error);
      setHasMore(true);
    } finally {
      setLoading(false);
    }
  };

  const resetSubmissionData = () => {
    setHasMore(true);
    setSubmissionsData([]);
    setNextPage(null);
  };

  return (
    <SubmissionsContext.Provider
      value={{
        submissionsData,
        setSubmissionData: setSubmissionsData,
        hiddenColumns,
        setHiddenColumns,
        filters,
        setFilters,
        updateFilters,
        loading,
        setLoading,
        fetchMoreSubmissions,
        hasMore,
        resetSubmissionData,
        fetchSubmissions,
        searchText,
        setSearchText,
        sortingCriteria,
        setSortingCriteria,
        filtersOptions,
        setFiltersOption,
        fetchAndSetFilterOption
      }}>
      {children}
    </SubmissionsContext.Provider>
  );
}
